import {ToastContent} from "components";
import {useAppDispatch, useAppSelector} from "hooks/index";
import {TProfilesImportInProgress, TUrlImportItem} from "mapx-components/BulkImport/types";
import {CalculateStatusTypeCounts} from "mapx-components/Modals/BulkImportModal/Report/utils";
import ProjectTabs from "mapx-pages/Project/ProjectTabs";
import {useCallback, useEffect, useRef} from "react";
import {useHistory} from "react-router-dom";
import {toast} from "react-toastify";
import {setImportLinkedInProfileInfoForCurrentImport} from "store/background-tasks/backgroundTaskActions";
import {getAllBackgroundBulkImports} from "store/background-tasks/backgroundTaskAsyncActions";
import {profilesImportsByLinkedInSelector} from "store/background-tasks/backgroundTaskSelectors";
import {
	removeBulkImportDataForSource,
	resetBulkImportStatesForProject,
	setLastCompleteCompanyImportForProject,
	setShouldOpenBulkImportReport,
	setShowLastImportReport,
} from "store/bulk-import/bulkImportActions";
import {getIndividualBulkImportStatuses} from "store/bulk-import/bulkImportAsyncActions";
import {lastCompletedCompanyImportForProjectSelector} from "store/bulk-import/bulkImportSelectors";
import {marketMapsSelector} from "store/market-map/marketMapSelectors";
import {setProjectTabIndex} from "store/project-list/projectListActions";
import {
	getProjectNameByProjectIDSelector,
	projectSelector,
} from "store/project-list/projectListSelectors";
import {
	fetchProjectSavedCompanyIDs,
	getPaginatedProjectSavedCompanies,
} from "store/project/projectCompaniesAsyncActions";
import {getTargetListCompanies} from "store/target-list/targetListAsyncActions";
import {TLinkedinBulkImportIndividualStatusResponse} from "../types";

/* eslint-disable  @typescript-eslint/no-explicit-any */

// Tracks the last known success count per background task ID
const lastKnownCompanySuccessCount: Record<number, number> = {};

/**
 * Hook to monitor the progress of bulk company imports into projects.
 * Handles tracking, updating state, and showing live notifications for completed imports.
 */
const useBulkImportCompanyProgressWatcher = () => {
	const history = useHistory();

	const state = useAppSelector((state) => state);

	const currentProject = useAppSelector(projectSelector);

	const lastCompletedCompanyImportForProject = useAppSelector(
		lastCompletedCompanyImportForProjectSelector,
	);

	const importLinkedInProfileForProjects = useAppSelector(profilesImportsByLinkedInSelector);

	const marketMaps = useAppSelector(marketMapsSelector);

	const dispatch = useAppDispatch();

	// Use Ref to persist intervals across renders without restarting them
	const intervalRefs = useRef<Record<number, any>>({});

	// Keep latest data in a ref so the interval can access it without being a dependency
	const latestDataRef = useRef<TProfilesImportInProgress[]>([]);
	useEffect(() => {
		latestDataRef.current = importLinkedInProfileForProjects;
	}, [importLinkedInProfileForProjects]);

	/**
	 * Navigates to the project and opens the Companies tab with the import report modal.
	 */
	const handleSeeProjectClick = useCallback(
		async (projectID: number) => {
			if (currentProject === null || currentProject?.id.toString() !== projectID.toString()) {
				dispatch(getPaginatedProjectSavedCompanies());
			}

			dispatch(setProjectTabIndex(ProjectTabs.savedCompaniesTabIndex));
			history.push(`/project/${projectID}`);

			// Use negative project ID for company report key
			dispatch(setShouldOpenBulkImportReport({id: -projectID, value: true}));

			toast.dismiss(`company_import_success_toaster${projectID}`);
			toast.dismiss(`company_import_error_toaster${projectID}`);
		},
		[dispatch, currentProject, history],
	);

	const isImportInProgress = (status: string) => {
		return status === "Created" || status === "In Progress" || status === "Scraped";
	};

	/**
	 * Updates the import information in the Redux store.
	 */
	const updateImportInfo = useCallback(
		async (response: TLinkedinBulkImportIndividualStatusResponse, purpose?: string) => {
			dispatch(
				setImportLinkedInProfileInfoForCurrentImport({
					...response.data,
					purpose: purpose || "import",
					import_type: "Companies Bulk Import",
				} as TProfilesImportInProgress),
			);
		},
		[dispatch],
	);

	const handleSuccessToast = useCallback(
		async (projectID: number, projectName: Nullable<string>) => {
			toast.success(ToastContent, {
				toastId: `company_import_success_toaster${projectID}`,
				autoClose: false,
				closeOnClick: false,
				data: {
					title: `Company import is completed for ${projectName}`,
					description: () => (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "2px",
								fontSize: "12px",
							}}
						>
							<p style={{width: "190px"}}>
								A company import has successfully completed without errors, click on
								report to see imported companies.
							</p>
							<button
								style={{
									backgroundColor: "#309161",
									color: "#ECF9F2",
									borderRadius: "5px",
									marginTop: "-20px",
									width: "60px",
									cursor: "pointer",
								}}
								onClick={() => handleSeeProjectClick(projectID)}
							>
								Report
							</button>
						</div>
					),
				},
				delay: 100,
			});
		},
		[handleSeeProjectClick],
	);

	const handleErrorToast = useCallback(
		async (projectID: number, projectName: Nullable<string>) => {
			toast.warning(ToastContent, {
				toastId: `company_import_error_toaster${projectID}`,
				autoClose: false,
				closeOnClick: false,
				data: {
					title: `Company import is completed with errors for ${projectName}`,
					description: () => (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "2px",
								fontSize: "12px",
							}}
						>
							<p style={{width: "190px"}}>
								A company import has completed with errors, click on report to see
								imported companies.
							</p>
							<button
								style={{
									backgroundColor: "#CC8800",
									color: "#ECF9F2",
									borderRadius: "5px",
									marginTop: "-20px",
									width: "60px",
									cursor: "pointer",
								}}
								onClick={() => handleSeeProjectClick(projectID)}
							>
								Report
							</button>
						</div>
					),
				},
				delay: 100,
			});
		},
		[handleSeeProjectClick],
	);

	/**
	 * Updates the redux state with latest completed company bulk import data
	 */
	const updateImportHistoryOnState = useCallback(
		(
			response: TLinkedinBulkImportIndividualStatusResponse,
			urls: TUrlImportItem[],
			marketMapId?: number,
		) => {
			const storageKey = marketMapId ?? response.data.project_id;
			const previouslyImportedData =
				lastCompletedCompanyImportForProject[storageKey]?.importInfo
					?.linkedin_company_urls || [];

			const mergedData = {
				id: storageKey,
				status: "Completed",
				importInfo: {
					linkedin_company_urls: [...urls],
				},
			};

			previouslyImportedData.forEach((item: TUrlImportItem) => {
				mergedData.importInfo.linkedin_company_urls.push(item);
			});

			const payload = {id: storageKey, data: mergedData};

			dispatch(setLastCompleteCompanyImportForProject(payload));
		},
		[lastCompletedCompanyImportForProject, dispatch],
	);

	const getImportSource = useCallback(
		(projectID: Nullable<number>): string => {
			if (projectID) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				return getProjectNameByProjectIDSelector(state, projectID);
			}

			return "Unknown";
		},
		[state],
	);

	const getMarketMapName = useCallback(
		(marketMapId: number): string => {
			const marketMap = marketMaps?.find((mm: any) => mm.id === marketMapId);

			return marketMap?.name || "Target list";
		},
		[marketMaps],
	);

	const handleMarketMapSuccessToast = useCallback((marketMapId: number, toastMessage: string) => {
		toast.success(ToastContent, {
			toastId: `company_import_success_toaster${marketMapId}`,
			autoClose: 8000,
			closeOnClick: true,
			data: {
				title: `Company import is completed for ${toastMessage}`,
				description: "All companies have been imported successfully.",
			},
			delay: 100,
		});
	}, []);

	const handleMarketMapErrorToast = useCallback((marketMapId: number, toastMessage: string) => {
		toast.warning(ToastContent, {
			toastId: `company_import_error_toaster${marketMapId}`,
			autoClose: 8000,
			closeOnClick: true,
			data: {
				title: `Company import completed with errors for ${toastMessage}`,
				description: "Some companies could not be imported. Check the report for details.",
			},
			delay: 100,
		});
	}, []);

	/**
	 * Handle post-completion actions: refresh company data and show toast
	 */
	const handlePostCompletionActions = useCallback(
		async (
			response: TLinkedinBulkImportIndividualStatusResponse,
			errorImportingCount: number,
			purpose?: string,
			marketMapId?: number,
		) => {
			const projectId = response.data.project_id;
			const isMarketMapImport = !!marketMapId;

			if (!isMarketMapImport) {
				dispatch(fetchProjectSavedCompanyIDs());

				if (projectId === currentProject?.id) {
					dispatch(getPaginatedProjectSavedCompanies());
				}
			}

			if (isMarketMapImport) {
				toast.dismiss(`market_map_company_import_started_toaster${marketMapId}`);
			} else {
				toast.dismiss(`import_started_toaster${projectId}`);
			}

			if (purpose === "import") {
				const urls = response.data.linkedin_company_urls || [];
				const statusCounts = CalculateStatusTypeCounts(urls);

				const toastMessage = isMarketMapImport
					? `Target list: ${getMarketMapName(marketMapId)}`
					: `Project: ${getImportSource(projectId)}`;

				const toastKey = isMarketMapImport ? marketMapId : projectId;

				if (statusCounts.inProgress > 0) {
					// Partial not handled for now — treat as in-progress
				} else if (errorImportingCount === 0) {
					if (isMarketMapImport) {
						handleMarketMapSuccessToast(toastKey, toastMessage);
					} else {
						await handleSuccessToast(toastKey, toastMessage);
					}
				} else {
					if (isMarketMapImport) {
						handleMarketMapErrorToast(toastKey, toastMessage);
					} else {
						await handleErrorToast(toastKey, toastMessage);
					}
				}
			}
		},
		[
			currentProject?.id,
			handleSuccessToast,
			handleErrorToast,
			handleMarketMapSuccessToast,
			handleMarketMapErrorToast,
			dispatch,
			getImportSource,
			getMarketMapName,
		],
	);

	/**
	 * Handles a completed company import
	 */
	const handleCompletedImport = useCallback(
		async (
			response: TLinkedinBulkImportIndividualStatusResponse,
			purpose?: string,
			marketMapId?: number,
		) => {
			const urls = response.data.linkedin_company_urls || [];
			updateImportHistoryOnState(response, urls, marketMapId);

			if (urls?.length > 0) {
				const errorImportingCount: number = urls.filter((item: TUrlImportItem) =>
					["bad url", "not found", "scraping error", "import error"].includes(
						item.status?.toLowerCase(),
					),
				).length;

				const stillInProgressImport: number = urls.filter((item: TUrlImportItem) =>
					["created", "in progress", "scraped"].includes(item.status?.toLowerCase()),
				).length;

				if (response.data.progress === 100 && stillInProgressImport === 0) {
					if (purpose === "import") {
						const isMarketMapImport = !!marketMapId;
						const importKey = isMarketMapImport
							? marketMapId
							: response.data.project_id;

						dispatch(removeBulkImportDataForSource(importKey));
						dispatch(resetBulkImportStatesForProject(importKey));
						dispatch(setShowLastImportReport(true));
						dispatch(getAllBackgroundBulkImports());

						if (isMarketMapImport) {
							dispatch(
								setShouldOpenBulkImportReport({id: -marketMapId, value: true}),
							);
						}
					}
				}

				await handlePostCompletionActions(
					response,
					errorImportingCount,
					purpose,
					marketMapId,
				);
			}

			const taskId = response.data?.id;
			if (intervalRefs.current[taskId]) {
				clearInterval(intervalRefs.current[taskId]);
				delete intervalRefs.current[taskId];
			}
		},
		[updateImportHistoryOnState, dispatch, handlePostCompletionActions],
	);

	/**
	 * Sets up polling to monitor ongoing company imports
	 */
	useEffect(() => {
		// Filter only company bulk imports
		const inProgressCompanyBulkImports =
			importLinkedInProfileForProjects?.filter(
				(imp: any) =>
					imp.import_type === "Companies Bulk Import" ||
					imp.type === "Companies Bulk Import",
			) || [];

		// Start intervals for new tasks
		inProgressCompanyBulkImports.forEach((data) => {
			if (data && isImportInProgress(data.status) && data.id) {
				const taskId = data.id;

				if (!intervalRefs.current[taskId]) {
					intervalRefs.current[taskId] = setInterval(async () => {
						try {
							const response = (await dispatch(
								getIndividualBulkImportStatuses(taskId) as any,
							)) as TLinkedinBulkImportIndividualStatusResponse;

							if (!response || "error" in response) {
								return;
							}

							// Get the latest purpose from our ref to ensure accuracy
							const latestData = latestDataRef.current.find((d) => d.id === taskId);
							const purpose = latestData?.purpose || data.purpose;

							await updateImportInfo(response, purpose);

							const urls = response.data?.linkedin_company_urls || [];
							const currentSuccessCount = urls.filter((item: TUrlImportItem) =>
								["success", "imported", "completed", "already exists"].includes(
									item.status?.toLowerCase(),
								),
							).length;

							const prevSuccessCount = lastKnownCompanySuccessCount[taskId] ?? 0;

							if (currentSuccessCount > prevSuccessCount) {
								lastKnownCompanySuccessCount[taskId] = currentSuccessCount;
								const marketMapId = latestData?.market_map_id ?? data.market_map_id;
								const isMarketMapImport = !!marketMapId;

								dispatch(getTargetListCompanies());

								if (!isMarketMapImport) {
									dispatch(fetchProjectSavedCompanyIDs());

									if (response.data?.project_id === currentProject?.id) {
										dispatch(getPaginatedProjectSavedCompanies());
									}
								}
							}

							if (response.status === "Completed") {
								delete lastKnownCompanySuccessCount[taskId];
								if (intervalRefs.current[taskId]) {
									clearInterval(intervalRefs.current[taskId]);
									delete intervalRefs.current[taskId];
								}
								const marketMapId =
									latestData?.market_map_id ?? data.market_map_id ?? undefined;
								await handleCompletedImport(response, purpose, marketMapId);
							}
						} catch (error) {
							console.error("Error fetching company bulk import status:", error);
						}
					}, 8 * 1000);
				}
			}
		});

		// Cleanup: identify tasks that are no longer in progress and stop their intervals
		const inProgressIds = new Set(inProgressCompanyBulkImports.map((d) => d.id));
		Object.keys(intervalRefs.current).forEach((idStr) => {
			const id = parseInt(idStr);
			if (!inProgressIds.has(id)) {
				clearInterval(intervalRefs.current[id]);
				delete intervalRefs.current[id];
			}
		});
	}, [
		importLinkedInProfileForProjects,
		dispatch,
		handleCompletedImport,
		updateImportInfo,
		currentProject?.id,
	]);

	// Final cleanup on unmount
	useEffect(() => {
		return () => {
			Object.values(intervalRefs.current).forEach(clearInterval);
			intervalRefs.current = {};
		};
	}, []);

	return null; // Hook doesn't return UI
};

export default useBulkImportCompanyProgressWatcher;
